/**
 * WizardProgressTracker Component
 *
 * Displays progress for the wizard-based evaluation pipeline.
 * Shows current stage, progress percentage, and estimated time remaining.
 */

import React from 'react';
import {
    Box,
    LinearProgress,
    Typography,
    Chip
} from '@mui/material';
import {
    VideoLibrary as VideoIcon,
    Assignment as EvaluationIcon,
    CheckCircle as CompleteIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import {
    CONSOLIDATED_PIPELINE_STATUS,
    getConsolidatedPipelineStatusLabel,
    getConsolidatedPipelineProgress,
    estimateRemainingTime
} from '../../../utils/api/consolidatedPipelineApi';

interface WizardProgressTrackerProps {
    status: string | null;
    error?: string | null;
}

const WizardProgressTracker: React.FC<WizardProgressTrackerProps> = ({ status, error }) => {
    const progress = status ? getConsolidatedPipelineProgress(status) : 0;
    const statusLabel = status ? getConsolidatedPipelineStatusLabel(status) : 'Waiting...';
    const estimatedTime = status ? estimateRemainingTime(status) : 0;

    // Get icon based on status
    const getStatusIcon = () => {
        if (status === CONSOLIDATED_PIPELINE_STATUS.FAILED_V2) {
            return <ErrorIcon color="error" />;
        }
        if (status === CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2) {
            return <CompleteIcon color="success" />;
        }
        if (status === CONSOLIDATED_PIPELINE_STATUS.PROCESSING_TRANSCRIPTION_V2 ||
            status === CONSOLIDATED_PIPELINE_STATUS.TRANSCRIPTION_COMPLETE_V2 ||
            status === 'PROCESSING_TRANSCRIPTION' ||
            status === 'TRANSCRIPTION_COMPLETE') { // V1 status compatibility
            return <VideoIcon color="primary" />;
        }
        return <EvaluationIcon color="primary" />;
    };

    // Get color based on status
    const getStatusColor = () => {
        if (status === CONSOLIDATED_PIPELINE_STATUS.FAILED_V2) {
            return 'error';
        }
        if (status === CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2) {
            return 'success';
        }
        return 'primary';
    };

    // Format time estimate
    const formatTimeEstimate = (seconds: number) => {
        if (seconds === 0) {
            return 'Complete';
        }
        if (seconds < 60) {
            return `~${seconds}s remaining`;
        }
        const minutes = Math.ceil(seconds / 60);
        return `~${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
    };

    return (
        <Box sx={{ width: '100%', py: 2 }}>
            {/* Status Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {getStatusIcon()}
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                        {statusLabel}
                    </Typography>
                    {estimatedTime > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {formatTimeEstimate(estimatedTime)}
                        </Typography>
                    )}
                </Box>
                <Chip
                    label={`${progress}%`}
                    color={getStatusColor() as any}
                    size="small"
                />
            </Box>

            {/* Progress Bar */}
            <LinearProgress
                variant={status === CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2 ? 'determinate' : 'indeterminate'}
                value={progress}
                color={getStatusColor() as any}
                sx={{
                    height: 8,
                    borderRadius: 1,
                    mb: error ? 2 : 0
                }}
            />

            {/* Error Message */}
            {error && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'error.light',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'error.main'
                    }}
                >
                    <Typography variant="body2" color="error.dark">
                        <strong>Error:</strong> {error}
                    </Typography>
                </Box>
            )}

            {/* Stage Details */}
            <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Pipeline Stages:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        size="small"
                        label="1. Upload"
                        color={progress >= 25 ? 'success' : 'default'}
                        variant={progress >= 25 ? 'filled' : 'outlined'}
                    />
                    <Chip
                        size="small"
                        label="2. Video Analysis"
                        color={progress >= 50 ? 'success' : progress >= 25 ? 'primary' : 'default'}
                        variant={progress >= 50 ? 'filled' : 'outlined'}
                    />
                    <Chip
                        size="small"
                        label="3. Evaluation"
                        color={progress >= 100 ? 'success' : progress >= 75 ? 'primary' : 'default'}
                        variant={progress >= 100 ? 'filled' : 'outlined'}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default WizardProgressTracker;
